import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { hideDialog } from '../../../dialog/actions';
import { IconCloseLarge } from '../../../icons/svg';
import { operatesWithEnterKey } from '../../functions.web';
import BaseDialog from './BaseDialog';
import Button from './Button';
import ClickableIcon from './ClickableIcon';
import { isMobileBrowser } from "../../../environment/utils";
import { setDesktopShareQualitySettingsVisibility } from "../../../../settings/actions.web";
import { getDesktopShareQualitySettingsVisibility } from "../../../../settings/functions.any";
const useStyles = makeStyles()(theme => {
    return {
        header: {
            width: '100%',
            padding: '20px 16px 16px 20px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            '&.is-mobile': {
                padding: '16px 16px 12px 16px',
            }
        },
        title: {
            fontFamily: 'Lato Regular',
            fontWeight: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            color: 'rgba(255, 255, 255, 0.75)',
            margin: 0,
            padding: 0,
            '&.is-mobile': {
                fontSize: '16px',
                lineHeight: '22px',
                color: 'rgba(255, 255, 255, 0.7)',
            }
        },
        content: {
            height: 'auto',
            overflowY: 'auto',
            width: '100%',
            boxSizing: 'border-box',
            padding: '0 16px',
            overflowX: 'hidden',
            '@media (max-width: 448px)': {
                height: '100%'
            }
        },
        footer: {
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: '24px',
            '& button:last-child': {
                marginLeft: '16px'
            }
        },
        footerLeftButtonBlock: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '24px',
        },
    };
});
const Dialog = ({ back = { hidden: true }, popoverButton, cancel = { translationKey: 'dialog.Cancel' }, customButton, children, className, classNameHeader, classNameHeaderTitle, classNameContent, classNameFooter, okButtonClassName, description, disableAutoHideOnSubmit = false, disableBackdropClose, hideCloseButton, disableEnter, disableEscape, ok = { translationKey: 'dialog.Ok' }, onCancel, onSubmit, size, testId, title, titleKey, position }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { is_in_picture_in_picture_mode } = useSelector((state) => state['features/picture-in-picture']);
    const isDesktopShareQualitySettingsVisible = useSelector((state) => Boolean(getDesktopShareQualitySettingsVisibility(state)));
    const isMobile = isMobileBrowser();
    const onClose = useCallback(() => {
        if (isDesktopShareQualitySettingsVisible) {
            dispatch(setDesktopShareQualitySettingsVisibility(false));
            return;
        }
        dispatch(hideDialog());
        onCancel?.();
    }, [onCancel, isDesktopShareQualitySettingsVisible]);
    const submit = useCallback(() => {
        if ((document.activeElement && !operatesWithEnterKey(document.activeElement)) || !document.activeElement) {
            !disableAutoHideOnSubmit && dispatch(hideDialog());
            onSubmit?.();
        }
    }, [onSubmit]);
    useEffect(() => {
        if (is_in_picture_in_picture_mode) {
            dispatch(hideDialog());
        }
    }, [is_in_picture_in_picture_mode]);
    return (React.createElement(BaseDialog, { className: className, description: description, disableBackdropClose: disableBackdropClose, disableEnter: disableEnter, disableEscape: disableEscape, onClose: onClose, size: size, submit: submit, testId: testId, title: title, titleKey: titleKey, position: position },
        (title !== undefined || titleKey !== undefined || !hideCloseButton) && (React.createElement("div", { className: cx(classes.header, classNameHeader, isMobile && 'is-mobile') },
            React.createElement("h1", { className: cx(classes.title, classNameHeaderTitle, isMobile && 'is-mobile'), id: 'dialog-title' }, title ?? t(titleKey ?? '')),
            !hideCloseButton && (React.createElement(ClickableIcon, { accessibilityLabel: t('dialog.accessibilityLabel.close'), icon: IconCloseLarge, id: 'modal-header-close-button', onClick: onClose })))),
        React.createElement("div", { className: cx(classes.content, classNameContent, isMobile && 'is-mobile'), "data-autofocus-inside": 'true' }, children),
        (!back.hidden || !cancel.hidden || !ok.hidden || customButton || popoverButton) && (React.createElement("div", { className: cx(classes.footer, classNameFooter), "data-autofocus-inside": 'true' },
            (popoverButton || customButton) && (React.createElement("div", { className: classes.footerLeftButtonBlock },
                popoverButton && popoverButton,
                customButton && customButton)),
            !back.hidden && React.createElement(Button, { accessibilityLabel: t(back.translationKey ?? ''), labelKey: back.translationKey, 
                // eslint-disable-next-line react/jsx-handler-names
                onClick: back.onClick, type: 'secondary' }),
            !cancel.hidden && React.createElement(Button, { accessibilityLabel: t(cancel.translationKey ?? ''), labelKey: cancel.translationKey, onClick: onClose, type: 'tertiary' }),
            !ok.hidden && React.createElement(Button, { accessibilityLabel: t(ok.translationKey ?? ''), disabled: ok.disabled, id: 'modal-dialog-ok-button', isSubmit: true, labelKey: ok.translationKey, className: okButtonClassName, ...(!ok.disabled && { onClick: submit }) })))));
};
export default Dialog;
